// Must stay the first import: see @/bootstrap/scrubUrlToken.
import '@/bootstrap/scrubUrlToken'
import { createApp } from '@/app'
import { createHead } from '@unhead/vue/client'
import { useFeatureFlagsStore } from '@/stores'
import { appConfig } from 'config/app'
import { recoverFromChunkError } from '@/utils/chunkError'
import {
  redactConsoleBreadcrumb,
  redactPiniaState,
  redactSentryValue,
  redactSpanAttributes,
  redactStaticSpan,
  redactUrlCredentials
} from '@/utils/sentryRedact'

// =============================================================================
// Global handlers for stale chunk loading, i.e. the browser asking for a JS or
// CSS chunk that a deployment has since replaced. See @/utils/chunkError.
// =============================================================================
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    // Prefer the Error object: it carries the name, which is how a bare
    // ChunkLoadError is recognized. It is null for cross-origin script errors.
    if (recoverFromChunkError(event.error ?? event.message)) {
      event.preventDefault()
    }
  })

  // Dynamic imports fail as rejected promises, so they never raise an `error` event
  window.addEventListener('unhandledrejection', (event) => {
    if (recoverFromChunkError(event.reason)) {
      event.preventDefault()
    }
  })
}

// Design system styles
import '@/styles/tokens.css'
import '@/styles/animations.css'
import '@/styles/forms.css'
import '@/styles/glass-cta.css'
import '@/styles/dashboard-common.css'
import '@/styles/dashboard-table.css'
import '@/styles/dashboard-forms.css'
import '@/styles/dashboard-light-theme.css'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import VueMatomo from 'vue-matomo'

// TypeScript declaration for SSR hydration
declare global {
  interface Window {
    __PINIA_STATE__?: string
  }
}

const { app, router, pinia } = createApp(false)
const head = createHead()
app.use(head)

// defineAsyncComponent failures never reach the window listeners above: Vue
// catches the loader rejection itself and routes it here, so a stale chunk
// behind a lazily loaded modal would otherwise just blank out. Sentry chains
// to this handler rather than replacing it, so registering it before Sentry
// initializes is safe.
app.config.errorHandler = (error, instance, info) => {
  if (recoverFromChunkError(error)) return
  // Mirrors what Vue logs by default, including the component, which is the
  // most useful part of the message when triaging locally.
  console.error(`[Vue] Unhandled error (${info}):`, error, instance)
}

// Hydrate Pinia state from SSR (if available)
// NOTE: Exclude jwt store - it reads from localStorage and SSR state would overwrite it with null
if (typeof window !== 'undefined' && window.__PINIA_STATE__) {
  const ssrState = JSON.parse(window.__PINIA_STATE__)
  delete ssrState.jwt // Don't hydrate jwt - it manages its own localStorage persistence
  pinia.state.value = ssrState
}

// Initialize Sentry (production only) - deferred to not block initial render.
// Headless crawlers get no Sentry at all — they hit timeouts and render paths
// no real user does, and skipping init also suppresses their transactions.
if (import.meta.env.PROD && !navigator.userAgent.includes('HeadlessChrome')) {
  const initSentry = () => {
    import('@sentry/vue').then((Sentry) => {
      // Credentials travel in URLs here and the SDK records URLs in several
      // places: scoped SSE tokens (the EventSource API can't send auth
      // headers), password reset and email verification links, the sub-account
      // invite token as a path segment, and the Stripe client secret on the
      // payment return pages. A reset or invite token in an event is an account
      // takeover primitive, so this runs on every URL the SDK keeps, not just
      // breadcrumbs. See @/utils/sentryRedact for how a parameter is judged.
      const stripToken = redactUrlCredentials

      // httpContextIntegration stamps both of these on every event type, error
      // and transaction alike, so both hooks have to call this. The referrer is
      // same-origin under our Referrer-Policy, which means it carries the full
      // path of the page the user came from: land on the dashboard from a
      // completed invite or password reset and the token is in it.
      const scrubRequest = (request?: { url?: string; headers?: { [key: string]: string } }) => {
        if (typeof request?.url === 'string') request.url = stripToken(request.url)
        if (typeof request?.headers?.Referer === 'string') {
          request.headers.Referer = stripToken(request.headers.Referer)
        }
      }

      const clientAddressKeys = ['forwarded', '-ip', 'remote-', 'via', '-user']

      Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
        // SDK v11's own baseline for what `sendDefaultPii: false` used to mean.
        // Left unset, v11 collects user info, cookies and HTTP bodies.
        dataCollection: {
          userInfo: false,
          cookies: false,
          httpHeaders: { deny: clientAddressKeys },
          httpBodies: [],
          urlQueryParams: { deny: clientAddressKeys },
          genAI: { inputs: false, outputs: false },
          databaseQueryData: false,
          queues: false,
          graphQL: { document: false, variables: false }
        },
        // Span streaming would bypass beforeSendTransaction for every span. In
        // the static lifecycle only INP spans leave outside a transaction, and
        // a static beforeSendSpan both scrubs them and keeps the SDK from
        // adding the page URL and raw Referer to them.
        traceLifecycle: 'static',
        beforeSendSpan: Sentry.withStaticSpan(redactStaticSpan),
        // Passing the router keeps the raw URL out of transaction names: without
        // it a pageload on /invite/<token> becomes the transaction name.
        integrations: [Sentry.browserTracingIntegration({ router })],
        tracePropagationTargets: ['localhost', 'cp.itsh.dev', /^\//],
        tracesSampleRate: import.meta.env.VITE_SENTRY_TRACE_RATE,
        beforeBreadcrumb(breadcrumb) {
          for (const field of ['url', 'to', 'from'] as const) {
            if (typeof breadcrumb.data?.[field] === 'string') {
              breadcrumb.data[field] = stripToken(breadcrumb.data[field])
            }
          }
          if (breadcrumb.category === 'console') redactConsoleBreadcrumb(breadcrumb)
          return breadcrumb
        },
        beforeSend(event) {
          // Chrome's built-in page translation reparents text nodes under
          // <font> wrappers, breaking the comment anchors Vue uses for
          // v-if/Transition fragments (vuejs/core#5513). Errors from
          // translated sessions are external DOM interference, not app bugs.
          const html = document.documentElement
          if (html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl')) {
            return null
          }

          scrubRequest(event.request)

          // The Vue integration attaches the failing component's props, which
          // for a contact or mailbox component is the record itself.
          const vueContext = event.contexts?.vue
          if (vueContext && 'propsData' in vueContext) {
            vueContext.propsData = redactSentryValue(vueContext.propsData)
          }

          return event
        },
        // beforeSend runs for error events only, so transactions need their own
        // pass over the same surfaces. On top of those, the vue-router
        // instrumentation copies every route param and query value into span
        // attributes, which on `invite/:token` means `params.token` and
        // `url.path.parameter.token` verbatim, and an http.client span records
        // the request URL under `url` (raw, path and query) as well as
        // `http.url`, so the query-stripped description does not cover it.
        beforeSendTransaction(event) {
          scrubRequest(event.request)

          if (typeof event.transaction === 'string') {
            event.transaction = stripToken(event.transaction)
          }

          const trace = event.contexts?.trace
          if (trace?.data) trace.data = redactSpanAttributes(trace.data)

          for (const span of event.spans ?? []) {
            if (typeof span.description === 'string') {
              span.description = stripToken(span.description)
            }
            if (span.data) span.data = redactSpanAttributes(span.data)
          }
          return event
        }
      })
      // The Pinia snapshot ships as both the `state` context and a full JSON
      // attachment, so tokens and contact data have to be stripped before the
      // SDK ever sees them. See @/utils/sentryRedact.
      pinia.use(Sentry.createSentryPiniaPlugin({ stateTransformer: redactPiniaState }))
    })
  }

  // Defer Sentry initialization until browser is idle
  if ('requestIdleCallback' in window) {
    requestIdleCallback(initSentry, { timeout: 5000 })
  } else {
    setTimeout(initSentry, 100)
  }
}

// Initialize feature flags (load from cache + fetch from API)
const featureFlagsStore = useFeatureFlagsStore()
featureFlagsStore.initialize()

// Initialize Matomo tracking (appConfig parses VITE_TRACKING as a real boolean;
// the raw env var is a string, where "false" would be truthy)
if (appConfig.env.trackingEnabled) {
  app.use(VueMatomo, {
    // Trim a trailing slash so the SDK doesn't build a double-slash URL (`host//matomo.js`).
    host: (import.meta.env.VITE_TRACK_URL ?? '').replace(/\/$/, ''),
    siteId: import.meta.env.VITE_TRACK_ID,
    router: router,
    disableCookies: true
  })
}

// Hide SSR loader after hydration
const hideLoader = () => {
  document.getElementById('ssr-loader')?.classList.add('hidden')
}

router.isReady().then(() => {
  app.mount('#app')
  hideLoader()
}).catch(() => {
  hideLoader()
})

// Fallback timeout
setTimeout(hideLoader, 5000)
